import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Accessibility,
  Check,
  Copy,
  GitBranch,
  Hash,
  LayoutTemplate,
  Loader2,
  MessageSquare,
  MousePointerClick,
  Sparkles,
  Target,
  Wand2,
} from 'lucide-react';
import {
  aiAPI,
  contentIntelligenceAPI,
  getErrorMessage,
  type AIComposeAssistResponse,
} from '../../services/api';

type ComposerAIAction =
  | 'generate'
  | 'improve'
  | 'platform_versions'
  | 'hashtags'
  | 'first_comment'
  | 'hook_cta'
  | 'accessibility';

interface BrandVoiceOption {
  id?: string;
  brand_voice_id?: string;
  name?: string;
  title?: string;
  is_default?: boolean;
}

interface ComposerAICommandCenterProps {
  caption: string;
  onCaptionChange: (text: string) => void;
  hashtags: string[];
  onHashtagsChange: (hashtags: string[]) => void;
  onFirstCommentChange: (text: string) => void;
  selectedPlatforms: string[];
  contentType: string;
  mediaContext?: string;
  onOverridesReplace: (overrides: Record<string, { text: string }>) => void;
  onOpenTemplates: () => void;
}

const ACTIONS: Array<{
  id: ComposerAIAction;
  icon: React.ElementType;
  labelKey: string;
  fallback: string;
}> = [
  {
    id: 'generate',
    icon: Sparkles,
    labelKey: 'comp_ai_cmd_generate',
    fallback: 'Generate',
  },
  {
    id: 'improve',
    icon: Wand2,
    labelKey: 'comp_ai_cmd_improve',
    fallback: 'Improve',
  },
  {
    id: 'platform_versions',
    icon: GitBranch,
    labelKey: 'comp_ai_cmd_versions',
    fallback: 'Versions',
  },
  {
    id: 'hashtags',
    icon: Hash,
    labelKey: 'comp_ai_cmd_hashtags',
    fallback: 'Hashtags',
  },
  {
    id: 'first_comment',
    icon: MessageSquare,
    labelKey: 'comp_ai_cmd_first_comment',
    fallback: 'First comment',
  },
  {
    id: 'hook_cta',
    icon: MousePointerClick,
    labelKey: 'comp_ai_cmd_hook_cta',
    fallback: 'Hook and CTA',
  },
  {
    id: 'accessibility',
    icon: Accessibility,
    labelKey: 'comp_ai_cmd_accessibility',
    fallback: 'Accessibility',
  },
];

function normalizeHashtags(tags?: string[]): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const raw of tags || []) {
    const tag = raw.replace(/^#/, '').trim();
    if (!tag || seen.has(tag.toLowerCase())) {
      continue;
    }
    seen.add(tag.toLowerCase());
    normalized.push(tag);
  }
  return normalized;
}

function normalizeVersions(
  versions?: AIComposeAssistResponse['platform_versions'],
): Record<string, { text: string }> {
  const out: Record<string, { text: string }> = {};
  if (!versions) {
    return out;
  }
  for (const [platform, value] of Object.entries(versions)) {
    const text =
      typeof value === 'string' ? value : String(value?.text || '').trim();
    if (text) {
      out[platform] = { text };
    }
  }
  return out;
}

export default function ComposerAICommandCenter({
  caption,
  onCaptionChange,
  hashtags,
  onHashtagsChange,
  onFirstCommentChange,
  selectedPlatforms,
  contentType,
  mediaContext,
  onOverridesReplace,
  onOpenTemplates,
}: ComposerAICommandCenterProps) {
  const { t, i18n } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [tone, setTone] = useState('brand');
  const [brandVoiceId, setBrandVoiceId] = useState('');
  const [result, setResult] = useState<AIComposeAssistResponse | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const brandVoicesQuery = useQuery({
    queryKey: ['composer-ai-brand-voices'],
    queryFn: async () => {
      const response = await contentIntelligenceAPI.listBrandVoices();
      const data = response.data as {
        brand_voices?: BrandVoiceOption[];
        items?: BrandVoiceOption[];
      };
      return data.brand_voices || data.items || [];
    },
    staleTime: 60_000,
  });

  const effectivePlatforms = useMemo(
    () => selectedPlatforms.length > 0 ? selectedPlatforms : ['instagram'],
    [selectedPlatforms],
  );

  const composeMutation = useMutation({
    mutationFn: async (action: ComposerAIAction) => {
      const response = await aiAPI.composeAssist({
        action,
        draft_text: caption,
        prompt,
        platforms: effectivePlatforms,
        tone,
        locale: i18n.language || 'en',
        brand_voice_id: brandVoiceId || undefined,
        content_type: contentType,
        media_context: mediaContext,
      });
      return response.data;
    },
    onSuccess: (data) => {
      setResult(data);
      toast.success(t('comp_ai_cmd_ready_toast', 'AI package ready'));
    },
    onError: (error) => {
      toast.error(
        getErrorMessage(
          error,
          t('comp_ai_cmd_failed', 'AI compose action failed'),
        ),
      );
    },
  });

  const selectedBrandVoice = useMemo(
    () =>
      brandVoicesQuery.data?.find(
        (voice) => (voice.brand_voice_id || voice.id) === brandVoiceId,
      ),
    [brandVoiceId, brandVoicesQuery.data],
  );

  const applyPrimary = () => {
    if (!result?.primary_text) {
      return;
    }
    onCaptionChange(result.primary_text);
    toast.success(t('comp_ai_cmd_caption_applied', 'Caption applied'));
  };

  const applyVersions = () => {
    const versions = normalizeVersions(result?.platform_versions);
    if (Object.keys(versions).length === 0) {
      return;
    }
    onOverridesReplace(versions);
    toast.success(
      t('comp_ai_cmd_versions_applied', {
        count: Object.keys(versions).length,
        defaultValue: 'Applied {{count}} platform version(s)',
      }),
    );
  };

  const applyHashtags = () => {
    const merged = normalizeHashtags([...hashtags, ...(result?.hashtags || [])]);
    if (merged.length === 0) {
      return;
    }
    onHashtagsChange(merged);
    toast.success(t('comp_ai_cmd_hashtags_applied', 'Hashtags applied'));
  };

  const applyFirstComment = () => {
    if (!result?.first_comment) {
      return;
    }
    onFirstCommentChange(result.first_comment);
    toast.success(
      t('comp_ai_cmd_first_comment_applied', 'First comment applied'),
    );
  };

  const applyPackage = () => {
    if (!result) {
      return;
    }
    applyPrimary();
    applyVersions();
    applyHashtags();
    applyFirstComment();
  };

  const copyText = async (id: string, text?: string) => {
    if (!text) {
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      toast.error(t('comp_ai_cmd_copy_failed', 'Failed to copy'));
    }
  };

  const canRun = Boolean(caption.trim() || prompt.trim());
  const brandVoices = brandVoicesQuery.data || [];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_160px_180px]">
        <label className="block">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            {t('comp_ai_cmd_prompt_label', 'Brief')}
          </span>
          <textarea
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            rows={3}
            className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-xs leading-relaxed text-slate-800 outline-none transition-colors focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
            placeholder={t(
              'comp_ai_cmd_prompt_placeholder',
              'Launch angle, audience, offer, or improvement request',
            )}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            {t('comp_ai_cmd_tone_label', 'Tone')}
          </span>
          <select
            value={tone}
            onChange={(event) => setTone(event.target.value)}
            className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-800 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
          >
            <option value="brand">{t('comp_ai_cmd_tone_brand', 'Brand')}</option>
            <option value="professional">
              {t('comp_ai_cmd_tone_professional', 'Professional')}
            </option>
            <option value="casual">{t('comp_ai_cmd_tone_casual', 'Casual')}</option>
            <option value="bold">{t('comp_ai_cmd_tone_bold', 'Bold')}</option>
            <option value="educational">
              {t('comp_ai_cmd_tone_educational', 'Educational')}
            </option>
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            {t('comp_ai_cmd_brand_voice_label', 'Brand voice')}
          </span>
          <select
            value={brandVoiceId}
            onChange={(event) => setBrandVoiceId(event.target.value)}
            className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-800 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
          >
            <option value="">{t('comp_ai_cmd_brand_voice_none', 'Default')}</option>
            {brandVoices.map((voice) => {
              const id = voice.brand_voice_id || voice.id || '';
              return (
                <option key={id} value={id}>
                  {voice.name || voice.title || id}
                  {voice.is_default ? ` ${t('comp_ai_cmd_default_badge', '(default)')}` : ''}
                </option>
              );
            })}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {ACTIONS.map((action) => {
          const Icon = action.icon;
          const active = composeMutation.variables === action.id;
          return (
            <button
              key={action.id}
              type="button"
              disabled={!canRun || composeMutation.isPending}
              onClick={() => composeMutation.mutate(action.id)}
              className="flex min-h-[38px] items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-45"
            >
              {active ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Icon className="h-3.5 w-3.5" />
              )}
              {t(action.labelKey, action.fallback)}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onOpenTemplates}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-100"
        >
          <LayoutTemplate className="h-3.5 w-3.5" />
          {t('comp_insert_template', 'Insert Template')}
        </button>
        {selectedBrandVoice && (
          <span className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-[11px] font-medium text-emerald-800">
            <Target className="h-3 w-3" />
            {selectedBrandVoice.name || selectedBrandVoice.title}
          </span>
        )}
      </div>

      {result && (
        <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-emerald-600" />
              <span className="text-xs font-semibold text-slate-800">
                {t('comp_ai_cmd_result_title', 'AI publish package')}
              </span>
              {typeof result.quality_score === 'number' && (
                <span className="rounded-md bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-600 ring-1 ring-slate-200">
                  {t('comp_ai_cmd_score', {
                    score: result.quality_score,
                    defaultValue: '{{score}}/100',
                  })}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={applyPackage}
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-slate-800"
            >
              <Check className="h-3.5 w-3.5" />
              {t('comp_ai_cmd_apply_package', 'Apply package')}
            </button>
          </div>

          {result.primary_text && (
            <section className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  {t('comp_ai_cmd_primary_title', 'Caption')}
                </span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => copyText('primary', result.primary_text)}
                    className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
                    title={t('comp_ai_cmd_copy', 'Copy')}
                  >
                    {copied === 'primary' ? (
                      <Check className="h-3.5 w-3.5" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={applyPrimary}
                    className="rounded-md px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                  >
                    {t('comp_ai_cmd_apply', 'Apply')}
                  </button>
                </div>
              </div>
              <p className="whitespace-pre-wrap text-xs leading-relaxed text-slate-800">
                {result.primary_text}
              </p>
            </section>
          )}

          {Object.keys(normalizeVersions(result.platform_versions)).length > 0 && (
            <section className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  {t('comp_ai_cmd_versions_title', 'Platform versions')}
                </span>
                <button
                  type="button"
                  onClick={applyVersions}
                  className="rounded-md px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                >
                  {t('comp_ai_cmd_apply_all', 'Apply all')}
                </button>
              </div>
              <div className="grid gap-2">
                {Object.entries(normalizeVersions(result.platform_versions)).map(
                  ([platform, value]) => (
                    <div key={platform} className="rounded-md bg-slate-50 p-2">
                      <div className="mb-1 text-[11px] font-semibold uppercase text-slate-500">
                        {platform}
                      </div>
                      <p className="whitespace-pre-wrap text-xs leading-relaxed text-slate-800">
                        {value.text}
                      </p>
                    </div>
                  ),
                )}
              </div>
            </section>
          )}

          <div className="grid gap-3 lg:grid-cols-2">
            {normalizeHashtags(result.hashtags).length > 0 && (
              <section className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    {t('comp_ai_cmd_hashtags_title', 'Hashtags')}
                  </span>
                  <button
                    type="button"
                    onClick={applyHashtags}
                    className="rounded-md px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                  >
                    {t('comp_ai_cmd_apply', 'Apply')}
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {normalizeHashtags(result.hashtags).map((tag) => (
                    <span
                      key={tag}
                      className="rounded-md bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-700"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {result.first_comment && (
              <section className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    {t('comp_ai_cmd_first_comment_title', 'First comment')}
                  </span>
                  <button
                    type="button"
                    onClick={applyFirstComment}
                    className="rounded-md px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                  >
                    {t('comp_ai_cmd_apply', 'Apply')}
                  </button>
                </div>
                <p className="whitespace-pre-wrap text-xs leading-relaxed text-slate-800">
                  {result.first_comment}
                </p>
              </section>
            )}
          </div>

          {(result.hooks?.length || result.cta_options?.length || result.alt_text) && (
            <section className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {t('comp_ai_cmd_extras_title', 'Hooks, CTAs, and accessibility')}
              </div>
              <div className="grid gap-3 lg:grid-cols-3">
                {result.hooks?.length ? (
                  <div>
                    <div className="mb-1 text-[11px] font-semibold text-slate-500">
                      {t('comp_ai_cmd_hooks_title', 'Hooks')}
                    </div>
                    <ul className="space-y-1 text-xs leading-relaxed text-slate-700">
                      {result.hooks.map((hook) => (
                        <li key={hook}>{hook}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {result.cta_options?.length ? (
                  <div>
                    <div className="mb-1 text-[11px] font-semibold text-slate-500">
                      {t('comp_ai_cmd_ctas_title', 'CTAs')}
                    </div>
                    <ul className="space-y-1 text-xs leading-relaxed text-slate-700">
                      {result.cta_options.map((cta) => (
                        <li key={cta}>{cta}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {result.alt_text ? (
                  <div>
                    <div className="mb-1 text-[11px] font-semibold text-slate-500">
                      {t('comp_ai_cmd_alt_title', 'Alt text')}
                    </div>
                    <p className="text-xs leading-relaxed text-slate-700">
                      {result.alt_text}
                    </p>
                  </div>
                ) : null}
              </div>
            </section>
          )}

          {result.review_notes?.length ? (
            <section className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {t('comp_ai_cmd_review_title', 'Review notes')}
              </div>
              <ul className="space-y-1 text-xs leading-relaxed text-slate-700">
                {result.review_notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
}
